import { useState, useEffect, useRef } from 'react';

interface ImageWithSkeletonProps {
  src: string;
  alt: string;
  className?: string;
  skeletonClassName?: string;
  darkMode?: boolean;
}

export default function ImageWithSkeleton({
  src,
  alt,
  className = '',
  skeletonClassName = '',
  darkMode = false,
}: ImageWithSkeletonProps) {
  // Track the "displayed" image (previous) and the "incoming" image (next)
  const [displayedSrc, setDisplayedSrc] = useState(src);
  const [incomingSrc, setIncomingSrc] = useState<string | null>(null);
  const [incomingLoaded, setIncomingLoaded] = useState(false);
  const [crossfading, setCrossfading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const prevSrcRef = useRef(src);

  // When src changes, start loading the new image in the background
  useEffect(() => {
    if (src === prevSrcRef.current) return;
    prevSrcRef.current = src;

    // Start loading the new image behind the scenes
    setIncomingSrc(src);
    setIncomingLoaded(false);
    setCrossfading(false);
  }, [src]);

  // When the incoming image finishes loading, trigger the crossfade
  const handleIncomingLoad = () => {
    setIncomingLoaded(true);
    setCrossfading(true);

    // After the crossfade completes, swap displayed → incoming and clean up
    setTimeout(() => {
      setDisplayedSrc(src);
      setIncomingSrc(null);
      setIncomingLoaded(false);
      setCrossfading(false);
    }, 600);
  };

  const showSkeleton = !initialLoaded && !crossfading;

  return (
    <div className={`relative w-full h-full overflow-hidden ${skeletonClassName}`}>

      {/* ── Skeleton (only on very first load) ── */}
      {showSkeleton && (
        <div
          className={`absolute inset-0 z-20 overflow-hidden ${
            darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}
        >
          {/* Shimmer sweep */}
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent" />

          {/* Skeleton room silhouette */}
          <div className="absolute inset-0 flex flex-col justify-end p-4 gap-2">
            <div className={`absolute top-0 left-0 right-0 h-1/3 rounded-b-3xl ${darkMode ? 'bg-gray-600/60' : 'bg-gray-200/80'}`} />
            <div className={`absolute bottom-0 left-0 right-0 h-1/4 ${darkMode ? 'bg-gray-600/40' : 'bg-gray-200/60'}`} />
            <div className="absolute bottom-[26%] left-[10%] w-[30%] h-[18%] rounded-xl opacity-60"
              style={{ background: darkMode ? '#374151' : '#e5e7eb' }} />
            <div className="absolute bottom-[26%] left-[45%] w-[20%] h-[12%] rounded-lg opacity-50"
              style={{ background: darkMode ? '#374151' : '#e5e7eb' }} />
            <div className="absolute bottom-[26%] right-[8%] w-[18%] h-[22%] rounded-xl opacity-55"
              style={{ background: darkMode ? '#374151' : '#e5e7eb' }} />
            <div className="absolute top-[8%] left-[30%] w-[40%] h-[28%] rounded-2xl opacity-40"
              style={{ background: darkMode ? '#4b5563' : '#f3f4f6' }} />
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full animate-pulse ${darkMode ? 'bg-teal-400' : 'bg-teal-500'}`} />
              <div className={`h-3 w-28 rounded-full animate-pulse ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            </div>
          </div>
        </div>
      )}

      {/* ── Currently displayed image (the "old" one during crossfade) ── */}
      <img
        key={`displayed-${displayedSrc}`}
        src={displayedSrc}
        alt={alt}
        onLoad={() => setInitialLoaded(true)}
        className={`absolute inset-0 ${className} transition-opacity duration-600 ${
          crossfading ? 'opacity-0' : initialLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: '600ms' }}
      />

      {/* ── Incoming image (fades in on top during crossfade) ── */}
      {incomingSrc && (
        <img
          key={`incoming-${incomingSrc}`}
          src={incomingSrc}
          alt={alt}
          onLoad={handleIncomingLoad}
          className={`absolute inset-0 ${className} transition-opacity duration-600 ${
            incomingLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDuration: '600ms' }}
        />
      )}

      {/* ── Style-change shimmer flash overlay ── */}
      {incomingSrc && !incomingLoaded && initialLoaded && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        </div>
      )}
    </div>
  );
}
